import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { getLibrary, getLibraryStats, deleteWorkEffort } from '../../api'
import { listFrom, loadErrorMessage } from '../../utils/loadState'
import './ThreadLibrary.css'

const STATE_LABELS = {
  complete: 'Complete',
  export: 'Exported',
  running: 'Running',
  plan: 'Planned',
  clarification: 'Needs Input',
  error: 'Error',
}
const STATE_COLORS = {
  complete: 'var(--green)',
  export: 'var(--green-dark)',
  running: 'var(--blue)',
  plan: 'var(--gray-400)',
  clarification: 'var(--amber)',
  error: 'var(--red)',
}

export default function ThreadLibrary() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [stats, setStats] = useState(null)
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList()
  const customerMap = Object.fromEntries(customers.map(c => [c.customer_id, c.company_name || c.customer_id]))
  const [loading, setLoading] = useState(true)
  // A failed library load is an error with a Retry, never "No threads found" (item 201).
  const [itemsError, setItemsError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Filters
  const [customerId, setCustomerId] = useState('')
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [sortBy, setSortBy] = useState('newest')

  // Library totals. A failed read is an error with a Retry where the strip goes -- it
  // used to hide the strip silently (item 201).
  const [statsError, setStatsError] = useState(null)
  async function loadStats() {
    setStatsError(null)
    try { setStats(await getLibraryStats()) }
    catch (err) { setStats(null); setStatsError(loadErrorMessage(err)) }
  }
  useEffect(() => { loadStats() }, [])

  // Load library items when filters change
  useEffect(() => {
    async function load() {
      setLoading(true)
      setItemsError(null)
      try {
        const params = { sort: sortBy }
        if (customerId) params.customer_id = customerId
        if (search.trim()) params.search = search.trim()
        if (stateFilter) params.state = stateFilter
        if (tagFilter) params.tag = tagFilter
        const data = await getLibrary(params)
        setItems(listFrom(data, 'items'))
      } catch (err) {
        console.error('Failed to load library:', err)
        setItems([])
        setItemsError(loadErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [customerId, search, stateFilter, tagFilter, sortBy, reloadKey])

  // Collect all tags from current items for the filter chips
  const allTags = useMemo(() => {
    const tags = new Set()
    items.forEach(item => (item.tags || []).forEach(t => tags.add(t)))
    return [...tags].sort()
  }, [items])

  function formatDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const diff = now - d
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`
    if (diff < 604800000) return `${Math.round(diff / 86400000)}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  function truncate(str, len) {
    if (!str) return ''
    return str.length > len ? str.slice(0, len) + '…' : str
  }

  const breadcrumbs = [{ label: 'Thread Library' }]
  const navActions = (
    <>
      <Link to="/customers" className="nav-btn">Customers</Link>
      <Link to="/exports" className="nav-btn">Export Center</Link>
    </>
  )

  return (
    <div className="tl-root">
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="tl-page">
        <div className="tl-container">

          {/* ── Stats Bar ──────────────────────────── */}
          {statsError && <LoadError inline what="the library totals" error={statsError} onRetry={loadStats} />}
          {stats && (
            <div className="tl-stats-bar">
              <div className="tl-stat">
                <div className="tl-stat-value">{stats.total_efforts}</div>
                <div className="tl-stat-label">Total</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value" style={{ color: 'var(--green)' }}>{stats.completed}</div>
                <div className="tl-stat-label">Completed</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value" style={{ color: 'var(--blue)' }}>{stats.in_progress}</div>
                <div className="tl-stat-label">In Progress</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value" style={{ color: 'var(--red)' }}>{stats.total_critical}</div>
                <div className="tl-stat-label">Critical</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value">{stats.total_findings}</div>
                <div className="tl-stat-label">Findings</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value">{stats.total_exports}</div>
                <div className="tl-stat-label">Exports</div>
              </div>
              <div className="tl-stat">
                <div className="tl-stat-value">{stats.customers_with_efforts}</div>
                <div className="tl-stat-label">Customers</div>
              </div>
            </div>
          )}

          {/* ── Header + Filters ──────────────────── */}
          <div className="tl-header">
            <div>
              <h1 className="tl-title">Thread Library</h1>
              <p className="tl-subtitle">Every investigation, analysis, and audit — searchable and reopenable</p>
            </div>
          </div>

          <div className="tl-filters">
            <input
              className="tl-search"
              type="text"
              placeholder="Search threads…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="tl-select" value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.customer_id} value={c.customer_id}>{c.company_name || c.customer_id}</option>
              ))}
            </select>
            {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
            <select className="tl-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
              <option value="">All States</option>
              <option value="complete">Complete</option>
              <option value="export">Exported</option>
              <option value="running">Running</option>
              <option value="clarification">Needs Input</option>
              <option value="error">Error</option>
              <option value="plan">Planned</option>
            </select>
            <select className="tl-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="most_findings">Most Findings</option>
              <option value="most_critical">Most Critical</option>
            </select>
          </div>

          {/* ── Tag Filter Chips ──────────────────── */}
          {allTags.length > 0 && (
            <div className="tl-tag-bar">
              <span className="tl-tag-bar-label">Tags:</span>
              {tagFilter && (
                <button
                  className="tl-tag-chip active"
                  onClick={() => setTagFilter('')}
                >
                  {tagFilter} ✕
                </button>
              )}
              {allTags.filter(t => t !== tagFilter).slice(0, 12).map(tag => (
                <button
                  key={tag}
                  className="tl-tag-chip"
                  onClick={() => setTagFilter(tag === tagFilter ? '' : tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* ── Active filter summary ─────────────── */}
          {(customerId || stateFilter || tagFilter || search) && (
            <div className="tl-active-filters">
              <span className="tl-active-label">Showing:</span>
              {customerId && (
                <span className="tl-filter-pill" onClick={() => setCustomerId('')}>
                  {customerMap[customerId] || customerId} ✕
                </span>
              )}
              {stateFilter && (
                <span className="tl-filter-pill" onClick={() => setStateFilter('')}>
                  {STATE_LABELS[stateFilter] || stateFilter} ✕
                </span>
              )}
              {tagFilter && (
                <span className="tl-filter-pill" onClick={() => setTagFilter('')}>
                  #{tagFilter} ✕
                </span>
              )}
              {search && (
                <span className="tl-filter-pill" onClick={() => setSearch('')}>
                  "{search}" ✕
                </span>
              )}
              <button className="tl-clear-all" onClick={() => {
                setCustomerId(''); setStateFilter(''); setTagFilter(''); setSearch('')
              }}>Clear all</button>
            </div>
          )}

          {/* ── Results ───────────────────────────── */}
          {loading ? (
            <div className="tl-loading"><div className="tl-spinner" /><p>Loading threads…</p></div>
          ) : itemsError ? (
            <LoadError what="threads" error={itemsError} onRetry={() => setReloadKey(k => k + 1)} />
          ) : items.length === 0 ? (
            <div className="tl-empty">
              <div className="tl-empty-icon">📋</div>
              <h3>No threads found</h3>
              <p>
                {search || customerId || stateFilter || tagFilter
                  ? 'Try adjusting your filters.'
                  : 'Complete a Work Effort investigation to see it here.'}
              </p>
              {!search && !customerId && !stateFilter && !tagFilter && (
                <button className="tl-empty-btn" onClick={() => navigate('/customers')}>
                  Go to Customers →
                </button>
              )}
            </div>
          ) : (
            <div className="tl-list">
              {/* Column headers */}
              <div className="tl-row tl-row-header">
                <div className="tl-col-title">Investigation</div>
                <div className="tl-col-customer">Customer</div>
                <div className="tl-col-findings">Findings</div>
                <div className="tl-col-state">Status</div>
                <div className="tl-col-date">Updated</div>
                <div className="tl-col-actions"></div>
              </div>

              {items.map(item => (
                <div
                  key={item.id}
                  className="tl-row tl-row-item"
                  onClick={() => navigate(`/customers/${item.customer_id}/work-efforts/${item.id}`)}
                >
                  <div className="tl-col-title">
                    <div className="tl-item-title">{item.title || 'Untitled'}</div>
                    <div className="tl-item-meta">
                      {item.step_count > 0 && <span>{item.step_count} steps</span>}
                      {item.message_count > 0 && <span>{item.message_count} msgs</span>}
                      {item.has_export && <span className="tl-export-badge">📄 Export</span>}
                      {item.tags && item.tags.length > 0 && item.tags.map(tag => (
                        <span key={tag} className="tl-tag">{tag}</span>
                      ))}
                    </div>
                    {item.notes && (
                      <div className="tl-item-notes">
                        💬 {truncate(item.notes, 90)}
                      </div>
                    )}
                  </div>

                  <div className="tl-col-customer">
                    <span className="tl-customer-name">{customerMap[item.customer_id] || item.customer_id}</span>
                  </div>

                  <div className="tl-col-findings">
                    {item.findings_count > 0 ? (
                      <div className="tl-findings-pills">
                        {item.critical_count > 0 && (
                          <span className="tl-pill critical">{item.critical_count} critical</span>
                        )}
                        {item.warning_count > 0 && (
                          <span className="tl-pill warning">{item.warning_count} warning</span>
                        )}
                        {item.compatible_count > 0 && (
                          <span className="tl-pill compatible">{item.compatible_count} ok</span>
                        )}
                        {item.blocked_count > 0 && (
                          <span className="tl-pill blocked">{item.blocked_count} blocked</span>
                        )}
                      </div>
                    ) : (
                      <span className="tl-no-findings">—</span>
                    )}
                  </div>

                  <div className="tl-col-state">
                    <span
                      className="tl-state-badge"
                      style={{ color: STATE_COLORS[item.state] || 'var(--gray-400)' }}
                    >
                      <span
                        className="tl-state-dot"
                        style={{ background: STATE_COLORS[item.state] || 'var(--gray-400)' }}
                      />
                      {STATE_LABELS[item.state] || item.state}
                    </span>
                  </div>

                  <div className="tl-col-date">
                    {formatDate(item.updated_at)}
                  </div>
                  <div className="tl-col-actions">
                    <button
                      className="tl-delete-btn"
                      title="Delete this work effort"
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (!window.confirm(`Delete "${item.title || 'Untitled'}"? This cannot be undone.`)) return
                        try {
                          await deleteWorkEffort(item.id)
                          setItems(prev => prev.filter(x => x.id !== item.id))
                        } catch (err) {
                          alert('Delete failed: ' + err.message)
                        }
                      }}
                    >🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
